/**
 * Settings → Calendar — V5-03 §1, reshaped by V6-07 §4.
 *
 * Three cards now: Google, Microsoft, and the ICS feed. The feed did not lose
 * its place when two-way sync arrived — it is still the only way to reach Apple
 * Calendar and everything else that speaks iCalendar — but it is no longer the
 * headline, so it is relabelled for what it is actually for.
 *
 * The page's real job has not changed: saying the awkward things before anyone
 * discovers them. For the feed, that a calendar app refreshes on its own
 * schedule. For a connection, that VirtueTasks overwrites what it owns.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { CalendarClock, Check, Copy, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api';
import { useCalendarConnections, useFeedStatus, useRegenerateFeed } from '@/features/calendar/api';
import { CalendarConnectionCard } from './CalendarConnectionCard';

interface Guide {
  app: string;
  steps: string[];
}

/** Written for someone who has never subscribed to a calendar feed before. */
const GUIDES: Guide[] = [
  {
    app: 'Google Calendar',
    steps: [
      'Open Google Calendar on a computer (this cannot be done in the mobile app).',
      'In the left sidebar, next to “Other calendars”, click + then “From URL”.',
      'Paste the URL above and click “Add calendar”.',
    ],
  },
  {
    app: 'Outlook / Microsoft 365',
    steps: [
      'Open Outlook on the web and go to Calendar.',
      'Click “Add calendar”, then “Subscribe from web”.',
      'Paste the URL above, give it a name, and click Import.',
    ],
  },
  {
    app: 'Apple Calendar (Mac, iPhone, iPad)',
    steps: [
      'On a Mac: Calendar → File → New Calendar Subscription.',
      'On an iPhone or iPad: Settings → Apps → Calendar → Calendar Accounts → Add Account → Other → Add Subscribed Calendar.',
      'Paste the URL above and confirm.',
    ],
  },
];

export function CalendarSettingsPage() {
  const { data: status, isLoading } = useFeedStatus();
  const { data: connections, isLoading: connectionsLoading } = useCalendarConnections();
  const regenerate = useRegenerateFeed();
  const [params, setParams] = useSearchParams();
  // Held in component state, never re-fetched: the server stores only a hash,
  // so this is the one and only time the URL can be shown.
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  /**
   * The OAuth callback lands here with a query string rather than a body — the
   * provider redirects a browser, so there is nowhere else to put the outcome.
   * It is turned into a toast and then removed from the URL, or a refresh would
   * replay the message for ever.
   */
  const outcome = params.get('calendar');
  useEffect(() => {
    if (!outcome) return;
    if (outcome === 'connected') {
      toast.success('Calendar connected — your tasks are being synced now.');
    } else {
      toast.error(
        `The calendar could not be connected: ${params.get('reason') ?? 'unknown reason'}`,
        { duration: 15_000 },
      );
    }
    setParams({}, { replace: true });
  }, [outcome, params, setParams]);

  async function issue(replacing: boolean) {
    try {
      const result = await regenerate.mutateAsync();
      setUrl(result.url);
      toast.success(replacing ? 'New calendar URL created' : 'Calendar URL created');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create the calendar URL');
    }
  }

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied');
    } catch {
      toast.error('Could not copy — select the text and copy it manually');
    }
  }

  return (
    <>
      <PageHeader
        title="Calendar"
        subtitle="Connect Google or Microsoft for two-way sync, or subscribe from any calendar app"
      />

      <div className="max-w-3xl space-y-6 p-4">
        {connectionsLoading ? (
          <Skeleton className="h-40" />
        ) : (
          <>
            <CalendarConnectionCard
              provider="google"
              configured={Boolean(connections?.configured.google)}
              connection={connections?.connections.find((c) => c.provider === 'google')}
            />
            <CalendarConnectionCard
              provider="microsoft"
              configured={Boolean(connections?.configured.microsoft)}
              connection={connections?.connections.find((c) => c.provider === 'microsoft')}
            />
          </>
        )}

        <section className="vt-surface p-4" data-testid="calendar-feed-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 text-[14px] font-medium">
                <CalendarClock className="size-4" />
                Calendar feed (ICS) — for Apple Calendar and other apps
              </h2>
              <p className="mt-1 max-w-lg text-[12px] vt-muted">
                A private, read-only link containing your assigned tasks with due dates, your
                approved leave, workspace holidays and your Calendly bookings. Anyone with the link
                can see all of that, so treat it like a password.
              </p>
            </div>
            {!isLoading && (
              <Badge tone={status?.isSet ? 'accent' : 'neutral'}>
                {status?.isSet ? 'Active' : 'Not created'}
              </Badge>
            )}
          </div>

          {url ? (
            <div className="mt-4 space-y-2">
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={url}
                  data-testid="calendar-feed-url"
                  onFocus={(e) => e.currentTarget.select()}
                  className="vt-mono text-[12px]"
                />
                <Button variant="secondary" onClick={copy} data-testid="calendar-feed-copy">
                  {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <p className="text-[12px] text-[var(--warn)]" data-testid="calendar-feed-once">
                Copy this now — it is stored encrypted and cannot be shown again. If you lose it,
                create a new one.
              </p>
            </div>
          ) : (
            <div className="mt-4 flex items-center gap-3">
              <Button
                onClick={() => issue(Boolean(status?.isSet))}
                disabled={regenerate.isPending}
                data-testid="calendar-feed-generate"
              >
                {regenerate.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RefreshCw className="size-4" />
                )}
                {status?.isSet ? 'Create a new URL' : 'Create my calendar URL'}
              </Button>
              {status?.isSet && (
                <p className="text-[12px] vt-muted" data-testid="calendar-feed-regenerate-note">
                  A URL already exists but cannot be displayed again. Creating a new one stops the
                  old one working immediately.
                </p>
              )}
            </div>
          )}
        </section>

        <section className="vt-surface p-4">
          <h2 className="text-[14px] font-medium">How to subscribe</h2>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            {GUIDES.map((guide) => (
              <div key={guide.app}>
                <h3 className="text-[13px] font-medium">{guide.app}</h3>
                <ol className="mt-1 list-decimal space-y-1 pl-4 text-[12px] vt-muted">
                  {guide.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </section>

        <section className="vt-surface p-4 text-[12px] vt-muted">
          <p data-testid="calendar-refresh-note">
            <strong className="text-[var(--text)]">Changes are not instant.</strong> Calendar apps
            refresh subscribed feeds on their own schedule — Google typically every few hours, and
            there is no way for us to make it check sooner. A task you add now may take a while to
            appear in your calendar.
          </p>
          <p className="mt-2" data-testid="calendar-twoway-note">
            <strong className="text-[var(--text)]">This feed is one-way.</strong> Editing one of
            these events in your calendar app changes nothing in VirtueTasks. For two-way sync —
            where moving a task to another day in your calendar moves its due date here — connect
            Google or Microsoft using the cards above.
          </p>
        </section>
      </div>
    </>
  );
}
